import { z } from 'zod';
/**
 * Worker disk usage.
 *
 * Workers measure the filesystem(s) holding their workspaces and report
 * them in each heartbeat. The master stores the latest report on the
 * worker; the CLI and UI display it. Reporting only — it has no effect
 * on dispatch.
 *
 * This module holds the shape and the pure helpers shared by the
 * workers (measuring), the master (storing) and the CLI/UI (display).
 */
/** One measured filesystem, identified by the path that was measured. */
export const WorkerDiskSchema = z.object({
    path: z.string().min(1).max(1024),
    totalBytes: z.number().int().nonnegative(),
    /** Bytes available to unprivileged processes (statfs `bavail`). */
    availableBytes: z.number().int().nonnegative(),
});
/** Maximum number of disks a worker may report. */
export const MAX_WORKER_DISKS = 16;
export const WorkerDisksSchema = z.array(WorkerDiskSchema).max(MAX_WORKER_DISKS);
const GB = 1024 ** 3;
/**
 * Default thresholds. Absolute sizes matter most (a build needs a few
 * GB whatever the disk size); ratios catch small disks.
 */
export const DEFAULT_DISK_THRESHOLDS = {
    warningBytes: 30 * GB,
    warningRatio: 0.15,
    criticalBytes: 10 * GB,
    criticalRatio: 0.05,
};
/** Fraction of the disk still available, in [0, 1]. */
export function diskAvailableRatio(disk) {
    return disk.totalBytes > 0 ? disk.availableBytes / disk.totalBytes : 0;
}
/** Classify a disk against thresholds. */
export function diskLevel(disk, thresholds = DEFAULT_DISK_THRESHOLDS) {
    const ratio = diskAvailableRatio(disk);
    if (disk.availableBytes < thresholds.criticalBytes || ratio < thresholds.criticalRatio) {
        return 'critical';
    }
    if (disk.availableBytes < thresholds.warningBytes || ratio < thresholds.warningRatio) {
        return 'warning';
    }
    return 'ok';
}
/** The disk with the least available space, or null when none. */
export function fullestDisk(disks) {
    return disks.reduce((worst, disk) => (worst === null || disk.availableBytes < worst.availableBytes ? disk : worst), null);
}
/** Human-readable binary size, e.g. `283 GB`, `1.5 TB`, `512 MB`. */
export function formatBytes(bytes) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    const digits = unit === 0 || value >= 10 ? 0 : 1;
    return `${value.toFixed(digits)} ${units[unit]}`;
}
/** Short summary of a disk, e.g. `283 GB free (31%)`. */
export function formatDiskAvailable(disk) {
    const percent = Math.round(diskAvailableRatio(disk) * 100);
    return `${formatBytes(disk.availableBytes)} free (${percent}%)`;
}
//# sourceMappingURL=disks.js.map